import qs from "qs";
import { ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Article, Guest, Publisher, MetadataObject, AboutPage } from "./types";
import EmptyImage from "./images/empty-image.jpg";
import { StaticImageData } from "next/image";

interface StrapiData {
  id: number;
  [key: string]: unknown; // Allow for any additional fields
}

interface StrapiResponse {
  data: StrapiData | StrapiData[];
}

export function spreadStrapiData(data: StrapiResponse): StrapiData | null {
  if (Array.isArray(data.data) && data.data.length > 0) {
    return data.data[0];
  }
  if (!Array.isArray(data.data)) {
    return data.data;
  }
  return null;
}

export const fetchArticles = async ({
  page,
  pageSize,
  filters,
}: {
  page: number;
  pageSize: number;
  filters: Record<string, unknown>;
}) => {
  return fetchContentType<{
    data: Article[];
    meta: { pagination: { total: number } };
  }>(
    "articles",
    {
      filters,
      pagination: {
        page,
        pageSize,
      },
      sort: ["publishedOn:desc"],
      populate: "*",
    },
    false,
  );
};

export default async function fetchContentType<T>(
  contentType: string,
  params: Record<string, unknown> = {},
  spreadData?: boolean,
): Promise<T | null> {
  // const { isEnabled } = await draftMode();

  try {
    const queryParams = { ...params };

    // if (isEnabled) {
    //   queryParams.status = "draft";
    // }

    // Construct the full URL for the API request
    const url = new URL(`/api/${contentType}`, process.env.NEXT_PUBLIC_API_URL);

    // Perform the fetch request with the provided query parameters
    const response = await fetch(`${url.href}?${qs.stringify(queryParams)}`, {
      method: "GET",
      cache: "force-cache",
      next: {
        revalidate: 30,
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch data from Strapi (url=${url.toString()}, status=${
          response.status
        })`,
      );
    }
    const jsonData: StrapiResponse = await response.json();

    return spreadData ? (spreadStrapiData(jsonData) as T) : (jsonData as T);
  } catch (error) {
    // Log any errors that occur during the fetch process
    console.error("FetchContentTypeError", error);
  }

  return null;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatNumber = (
  number: number,
  locale: string = "en-US",
): string => {
  return new Intl.NumberFormat(locale, {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);
};

export function getImage(
  object: Guest | Article | Publisher | AboutPage | null,
  prop: keyof Guest | keyof Article | keyof Publisher = "image",
  format: "thumbnail" | "small" | "medium" | "large" = "small",
  fallbackImage: string | StaticImageData = EmptyImage,
) {
  if (!object) return fallbackImage;

  return object[prop]?.formats?.[format]?.url
    ? strapiImage(object[prop].formats[format].url)
    : object[prop]?.url
      ? strapiImage(object[prop].url)
      : fallbackImage;
}

export function getArticleImage(article: Article): string | StaticImageData {
  return getImage(
    article,
    "bookImage",
    "medium",
    article.bookImageUrl?.startsWith("https://") &&
      !article.bookImageUrl?.endsWith("book_no_image_v2.jpg")
      ? article.bookImageUrl
      : EmptyImage,
  );
}

export function strapiImage(url: string): string {
  if ("string" === typeof url && url.startsWith("/")) {
    if (
      !process.env.NEXT_PUBLIC_API_URL &&
      document?.location.host.endsWith(".strapidemo.com")
    ) {
      return `https://${document.location.host.replace(
        "client-",
        "api-",
      )}${url}`;
    }

    return process.env.NEXT_PUBLIC_API_URL + url;
  }
  return url;
}

export function formatNamesList(guests: Guest[]): string {
  if (guests.length === 0) return "";
  if (guests.length === 1) return guests[0].name;
  if (guests.length === 2) return `${guests[0].name} and ${guests[1].name}`;

  const lastItem = guests[guests.length - 1].name;
  const otherItems = guests.map((g) => g.name).slice(0, -1);
  return `${otherItems.join(", ")} and ${lastItem}`;
}

export function formatDate(date: string) {
  return new Date(date).toLocaleDateString();
}

export function generateMetadataObject(
  seo?: MetadataObject,
  defaultTitle = "Indian Religions Podcast",
  defaultDescription = "Your source for the latest news and insights in Indian religions",
  defaultImage = "",
) {
  const defaultKeywords = "Indian religions, podcast, news, insights";

  return {
    title: seo?.metaTitle || defaultTitle,
    description: seo?.metaDescription || defaultDescription,
    keywords: seo?.keywords || defaultKeywords,
    openGraph: {
      title: seo?.metaTitle || defaultTitle,
      description: seo?.metaDescription || defaultDescription,
      images: seo?.metaImage
        ? [{ url: strapiImage(seo?.metaImage.url) }]
        : defaultImage,
    },
    twitter: {
      card: "summary_large_image",
      title: seo?.metaTitle || defaultTitle,
      description: seo?.metaDescription || defaultDescription,
      images: seo?.metaImage
        ? [{ url: strapiImage(seo?.metaImage.url) }]
        : defaultImage,
    },
  };
}
